import { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faTimeline } from '@fortawesome/free-solid-svg-icons';
import './PatientLifeLine.scss';

const STATUS_CONFIG = {
  done: { label: 'Effectué', dotClass: 'tl-dot--done', badgeClass: 'tl-badge--done' },
  pending: { label: 'En attente', dotClass: 'tl-dot--pending', badgeClass: 'tl-badge--pending' },
  cancelled: { label: 'Annulé', dotClass: 'tl-dot--cancelled', badgeClass: 'tl-badge--cancelled' },
};

const PatientLifeLine = ({ events = [] }) => {
  const clipRef = useRef(null);
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const sorted = [...events].sort((a, b) => new Date(b.date) - new Date(a.date));

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  // Drag-to-scroll — re-run quand sorted.length change (données async)
  useEffect(() => {
    const clip = clipRef.current;
    const sc = scrollRef.current;
    if (!clip || !sc) return;

    const onMouseDown = (e) => {
      isDragging.current = true;
      startX.current = e.pageX;
      scrollLeft.current = sc.scrollLeft;
      clip.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      sc.scrollLeft = scrollLeft.current - (e.pageX - startX.current) * 1.5;
    };

    const stopDrag = () => {
      isDragging.current = false;
      clip.style.cursor = 'grab';
    };

    clip.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopDrag);

    return () => {
      clip.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDrag);
    };
  }, [sorted.length]);

  // Scroll hint au chargement — re-run quand les données arrivent
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const t1 = setTimeout(() => sc.scrollBy({ left: 80, behavior: 'smooth' }), 800);
    const t2 = setTimeout(() => sc.scrollBy({ left: -80, behavior: 'smooth' }), 1300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [sorted.length]);

  const scrollBy = (dx) =>
    scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  // État vide — on garde le composant monté pour que les refs existent
  if (!sorted.length) {
    return (
      <div className="tl-outer tl-empty">
        <FontAwesomeIcon icon={faTimeline} className="tl-empty__icon" />
        <span>Aucun événement à afficher</span>
      </div>
    );
  }

  return (
    <div className="tl-outer">
      <button className="tl-nav tl-nav--left" onClick={() => scrollBy(-190)} aria-label="Défiler à gauche">
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>

      <div className="tl-clip" ref={clipRef} style={{ cursor: 'grab' }}>
        <div className="tl-scroll" ref={scrollRef}>
          <div className="tl-track">
            {sorted.map((ev, i) => {
              const cfg = STATUS_CONFIG[ev.status] ?? STATUS_CONFIG.pending;
              return (
                <div className="tl-item" key={i}>
                  <div className="tl-label">
                    <span className="tl-label__name">{ev.label}</span>
                    <span className="tl-label__date">{formatDate(ev.date)}</span>
                  </div>

                  <div className="tl-dot-wrap">
                    <div className={`tl-dot ${cfg.dotClass}`} />
                    {i < sorted.length - 1 && <div className="tl-connector" />}
                  </div>

                  <span className={`tl-badge ${cfg.badgeClass}`}>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <button className="tl-nav tl-nav--right" onClick={() => scrollBy(190)} aria-label="Défiler à droite">
        <FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
  );
};

export default PatientLifeLine;